import { useEffect, useMemo, useRef, useState } from 'react'
import { BookOpen } from 'lucide-react'
import type { PlayPreview } from '@/types/Content'
import { CanvasThumbnail } from '@/components/canvas-renderer'
import { PREVIEW_LAZY_LOAD_MARGIN, PREVIEW_PLAY_LIMIT } from '@/constants/Preview'
import './playbook-preview-grid.css'

type PlaybookPreviewGridProps = {
	previewPlays: PlayPreview[]
	playCount: number
}

export function PlaybookPreviewGrid({ previewPlays, playCount: _playCount }: PlaybookPreviewGridProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const [isVisible, setIsVisible] = useState(false)

	// Handle 1-3 plays: fill remaining slots with placeholders
	const gridItems = useMemo(() => {
		const items = []
		for (let i = 0; i < PREVIEW_PLAY_LIMIT; i++) {
			if (i < previewPlays.length) {
				items.push({ type: 'play' as const, data: previewPlays[i] })
			} else {
				items.push({ type: 'empty' as const })
			}
		}
		return items
	}, [previewPlays])

	// Lazy loading with IntersectionObserver
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				if (entry?.isIntersecting) {
					setIsVisible(true)
					observer.disconnect()
				}
			},
			{ rootMargin: PREVIEW_LAZY_LOAD_MARGIN },
		)

		if (containerRef.current) {
			observer.observe(containerRef.current)
		}

		return () => observer.disconnect()
	}, [])

	// Fallback: No plays - show gradient with icon
	if (previewPlays.length === 0) {
		return (
			<div className="playbook-preview-empty">
				<BookOpen className="playbook-preview-empty-icon" />
			</div>
		)
	}

	return (
		<div ref={containerRef} className="playbook-preview-grid">
			{isVisible && gridItems.map((item, index) => (
				<div key={index} className="playbook-preview-cell">
					{item.type === 'play' && item.data ? (
						<CanvasThumbnail
							blockId={item.data.id}
						/>
					) : (
						<div className="playbook-preview-placeholder" />
					)}
				</div>
			))}
		</div>
	)
}
